/// <reference lib="webworker" />

type WorkerRequest = {
  id: string;
  type: 'estimate';
  imageUrl: string;
};

type ProgressPayload = {
  status?: string;
  file?: string;
  progress?: number;
};

const MODEL_ID = 'onnx-community/depth-anything-v2-small';
const TRANSFORMERS_JS_URL = 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@4.2.0';
let estimatorPromise: Promise<any> | null = null;
let activeBackend: 'local-webgpu' | 'local-wasm' = 'local-wasm';

const postProgress = (id: string, progress: number, label: string) => {
  self.postMessage({ type: 'progress', id, progress, label });
};

async function createEstimator(id: string) {
  // Keep the optional local-AI runtime out of the standard 2D application
  // bundle. The model and runtime are fetched only after Create Depth is used.
  const { pipeline } = await import(/* @vite-ignore */ TRANSFORMERS_JS_URL) as { pipeline: (...args: any[]) => Promise<any> };
  const supportsWebGpu = Boolean((self.navigator as Navigator & { gpu?: unknown }).gpu);
  const progressCallback = (payload: ProgressPayload) => {
    const raw = Number(payload.progress);
    const normalized = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
    const fileLabel = payload.file ? ` · ${payload.file.split('/').pop()}` : '';
    postProgress(id, 5 + normalized * 0.5, `Loading local depth model${fileLabel}`);
  };

  if (supportsWebGpu) {
    try {
      activeBackend = 'local-webgpu';
      return await pipeline('depth-estimation', MODEL_ID, {
        device: 'webgpu',
        dtype: 'q4',
        progress_callback: progressCallback,
      } as any);
    } catch (error) {
      console.warn('[HDRI depth] WebGPU initialization failed; using WASM.', error);
    }
  }

  activeBackend = 'local-wasm';
  return await pipeline('depth-estimation', MODEL_ID, {
    device: 'wasm',
    dtype: 'q8',
    progress_callback: progressCallback,
  } as any);
}

self.addEventListener('message', async (event: MessageEvent<WorkerRequest>) => {
  const request = event.data;
  if (!request || request.type !== 'estimate' || !request.id || !request.imageUrl) return;

  try {
    postProgress(request.id, 2, 'Preparing local depth model');
    estimatorPromise ||= createEstimator(request.id);
    const estimator = await estimatorPromise;
    postProgress(request.id, 58, 'Estimating panoramic depth');
    const output = await estimator(request.imageUrl);
    const tensor = output?.predicted_depth;
    const dims = Array.isArray(tensor?.dims) ? tensor.dims : [];
    const height = Number(dims[dims.length - 2]);
    const width = Number(dims[dims.length - 1]);
    if (!tensor?.data || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      throw new Error('The local depth model returned an invalid prediction.');
    }
    const values = Float32Array.from(tensor.data as ArrayLike<number>);
    postProgress(request.id, 92, 'Finishing depth estimate');
    self.postMessage({
      type: 'result',
      id: request.id,
      width,
      height,
      backend: activeBackend,
      model: MODEL_ID,
      values: values.buffer,
    }, [values.buffer]);
  } catch (error) {
    estimatorPromise = null;
    self.postMessage({
      type: 'error',
      id: request.id,
      message: error instanceof Error ? error.message : 'Local depth inference failed.',
    });
  }
});

export {};
